import React, { useEffect } from 'react';
import PageLayout from '../../components/PageLayout';
import useSWR, { mutate } from 'swr';
import {
  useHistory,
  useParams,
} from 'react-router-dom/cjs/react-router-dom.min';
import { formatCurrencyV2, useReducerSpread } from '../../utils/common.util';
import { MoreVertical, Plus } from 'react-feather';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { ConfirmModal } from '../../components/modal/confirm-modal/ConfirmModal.component';
import { TextInput } from 'carbon-components-react';
import { deleteMethod, postMethod } from '../../api/common';
import Dropdown from '../../components/dropdown/Dropdown';

export default function SingleGroupFeeTypePage() {
  const { id } = useParams();

  // fetch current fee type
  const singleFeeTypeUrl = `daily-billing/fees/${id}/summary`;
  const { data: d, isLoading } = useSWR(singleFeeTypeUrl);
  const data = d?.dailyBillingFee ?? {};

  const [isOpen, setIsOpen] = React.useState('');

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: 'Fee Types', to: '/daily-fees/types' },
        { label: data?.name, active: true },
      ]}
      isLoading={isLoading}
      title={data?.name || 'Fee Type'}
    >
      {!data.name && (
        <p className="text-14 text-grey">Fee type does not exist.</p>
      )}
      <div
        className="d-flex-space-between"
        style={{ gap: '8px', marginBottom: '16px', alignItems: 'center' }}
      >
        <div>
          <span className="text-12 text-dark">Amount: </span>
          <span className="text-16 font-bold">
            {formatCurrencyV2(data?.amount)}
          </span>
        </div>
        <button
          className="text-12 text-primary font-bold pointer clean-btn "
          style={{ gap: '4px' }}
          onClick={() => setIsOpen('create')}
        >
          <Plus size="11" strokeWidth="5px" />
          &nbsp;
          <span>Add new group</span>
        </button>
      </div>

      {data?.genericAmounts?.length > 0 && (
        <table className="bx--data-table table-white">
          <thead>
            <tr>
              <th>Name</th>
              <th>Amount (GHS)</th>
              <th>Number of Students</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {data?.genericAmounts?.map((item) => {
              return (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.amount}</td>
                  <td>{item.numberOfStudents}</td>
                  <td>
                    <ActionsCell item={item} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {data?.genericAmounts?.length === 0 && (
        <p className="text-14 text-grey">
          No groups created under this fee type yet.
        </p>
      )}

      <CreateGroupUnderFeeType
        isOpen={isOpen === 'create'}
        closeModal={() => setIsOpen('')}
        feeType={data}
      />
    </PageLayout>
  );
}

function ActionsCell({ item }) {
  const { id } = useParams();
  const history = useHistory();

  const [isOpen, setIsOpen] = React.useState('');

  const actions = [
    {
      label: 'Students',
      onClick: () =>
        history.push(`/daily-fees/group-types/${id}/group/${item?.id}`),
    },
    { label: 'Edit', onClick: () => setIsOpen('edit') },
    { label: 'Delete', onClick: () => setIsOpen('delete') },
  ];

  const { trigger, error, isMutating } = useCustomMutation(
    `daily-billing/fees/generic-grouping/${item?.id}/delete`,
    (url, { arg }) => deleteMethod(url, arg),
  );
  return (
    <>
      <Dropdown actions={actions}>
        <span style={{ cursor: 'pointer' }}>
          <MoreVertical />
        </span>
      </Dropdown>

      <ConfirmModal
        isOpen={isOpen}
        closeModal={() => setIsOpen('')}
        title={`Delete Group - ${item?.name}`}
        actionText={'DELETE'}
        description="Are you sure you want to delete this group? This action cannot be undone."
        onClick={() =>
          trigger(null, {
            onSuccess() {
              setIsOpen('');
              mutate(`daily-billing/fees/${item?.dailyBillingFee?.id}/summary`);
            },
          })
        }
        isLoading={isMutating}
        errorMessage={error ? error.message : null}
        actionBtnClasses="danger"
      />

      <EditGroupUnderFeeType
        isOpen={isOpen === 'edit'}
        closeModal={() => setIsOpen('')}
        feeType={item?.dailyBillingFee}
        data={item}
      />
    </>
  );
}

const groupInitialData = {
  name: '',
  amount: '',
  dailyBillingFeeId: null,
  genericGroupingId: null,
};
export function GroupUnderFeeTypeForm({
  isOpen,
  closeModal,
  mutation,
  data,
  feeType,
}) {
  const [formData, setFormData] = useReducerSpread(groupInitialData);

  useEffect(() => {
    if (!isOpen) return; // Only run when modal is open
    if (data) {
      setFormData({
        amount: data?.amount,
        name: data?.name,
        genericGroupingId: data?.id,
        dailyBillingFeeId: feeType?.id,
      });
    } else {
      setFormData({ ...groupInitialData, dailyBillingFeeId: feeType?.id });
    }
  }, [isOpen, data, setFormData, feeType?.id]);

  function handleClose() {
    setFormData(groupInitialData);
    mutation?.reset();
    closeModal();
  }

  return (
    <ConfirmModal
      isOpen={isOpen}
      closeModal={closeModal}
      title={`${data ? 'Edit' : 'Create'} Group Under ${feeType?.name}`}
      description={
        <div>
          <TextInput
            id="name"
            name="name"
            labelText="Fee Name"
            placeholder="Enter group name"
            className="mb-20"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
          />

          <TextInput
            id={`group-fee-amount`}
            name={`group-fee-amount`}
            type="number"
            labelText="Amount"
            placeholder="Enter amount"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ amount: parseFloat(e.target.value) })
            }
          />
        </div>
      }
      actionText={data ? 'UPDATE' : 'CREATE'}
      onClick={() =>
        mutation?.trigger(formData, {
          onSuccess() {
            handleClose();
            mutate(`daily-billing/fees/${feeType?.id}/summary`);
          },
        })
      }
      disabled={mutation?.isMutating || !formData.name || !formData.amount}
      isLoading={mutation?.isMutating}
      errorMessage={mutation?.error ? mutation?.error.message : null}
      actionBtnClasses="primary"
    />
  );
}

export function CreateGroupUnderFeeType({ isOpen, closeModal, feeType }) {
  const create = useCustomMutation(
    'daily-billing/fees/generic-grouping/create-or-update',
    (url, { arg }) => postMethod(url, arg),
  );

  return (
    <GroupUnderFeeTypeForm
      isOpen={isOpen}
      closeModal={closeModal}
      mutation={create}
      feeType={feeType}
    />
  );
}

export function EditGroupUnderFeeType({ isOpen, closeModal, data, feeType }) {
  const edit = useCustomMutation(
    `daily-billing/fees/generic-grouping/create-or-update`,
    (url, { arg }) => postMethod(url, arg),
  );

  return (
    <GroupUnderFeeTypeForm
      isOpen={isOpen}
      closeModal={closeModal}
      mutation={edit}
      data={data}
      feeType={feeType}
    />
  );
}
